// front-end/multimodal/src/StoryPlayer.tsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useRef, useCallback, FC } from 'react'; // Added React, FC
import './StoryPlayer.css';
import AudioPlayer from './AudioPlayer';
import HiddenAudioPlayer from './HiddenAudioPlayer';
import DictionaryWord from './DictionaryWord';
import CloseIcon from './CloseIcon';
import FontSelector from './FontSelector';
import { MyContext } from "./Context";
// Add type imports
import type { Story, Sentence, Word, PlayerConfig, ProgressState, DictionaryData, AudioConfig, Voice } from './types/story.ts';
import { getStorageBoolean, getStorageJSON, getClosestSpeakingRate } from './utils'; // Import from utils

interface StoryPlayerProps {
  uri: string;
  onClose: () => void;
  languageUrl: string;
}

const StoryPlayer: FC<StoryPlayerProps> = ({ uri, onClose, languageUrl }) => { 
    const domainMatchArr = uri.match(/^(https?:\/\/[^/]+|[^/]+)/);
    const domain = domainMatchArr ? domainMatchArr[0] + "/" : "/"; // Handle null match
    const storyPathMatch = uri.match(/\/stories\/([^/]+)\/story\.json/);
    const storyPath = storyPathMatch ? storyPathMatch[1] : '';
    
    const { isScriptFontState } = useContext(MyContext); // Removed :any, type is inferred from MyContextType
    const [isCopied, setIsCopied] = useState(false);
    const [audioPlayers, setAudioPlayers] = useState<PlayerConfig[]>(() => getStorageJSON<PlayerConfig[]>('audioPlayers', [{ languageCode: 'th', speakingRate: 1.0 }]));
    const [story, setStory] = useState<Story | null>(null);
    const [dictionaryExplanation, setDictionaryExplanation] = useState<DictionaryData | null>(null);
    const [sentenceIndex, setSentenceIndex] = useState(() => {
        const cacheKey = 'sentenceIndex_' + uri;
        const savedIndex = localStorage.getItem(cacheKey);
        return savedIndex !== null ? parseInt(savedIndex, 10) : 0;
    });
    const sentenceIndexRef = useRef<number>(sentenceIndex);
    const [focusedWordIndex, setFocusedWordIndex] = useState(-1);
    const [dictionaryWord, setDictionaryWord] = useState<string | null>(null);
    const [introPlaying, setIntroPlaying] = useState(false);
    const [introMid, setIntroMid] = useState(false);
    const [introFinished, setIntroFinished] = useState(false);
    const [introPlayedAlready, setIntroPlayedAlready] = useState(false);
    const [autoStepForward, setAutoStepForward] = useState(getStorageBoolean('autoStepForward', false));
    
    const autoStepForwardRef = useRef<boolean>(autoStepForward);

    const thaiContainerRef = useRef<HTMLDivElement | null>(null);
    const englishWordsRef = useRef<HTMLDivElement | null>(null);
    const englishSentenceRef = useRef<HTMLDivElement | null>(null);

    const clearHighlights = useCallback(() => {
        if (!thaiContainerRef.current || !englishWordsRef.current || !englishSentenceRef.current) return;
    
        const thaiWords = thaiContainerRef.current.children[0].children;
        const englishWords = englishWordsRef.current.children[0].children;
    
        Array.from(thaiWords).forEach((word: Element) => word.classList.remove('highlight-base', 'highlight-in', 'highlight-out'));
        Array.from(englishWords).forEach((word: Element) => word.classList.remove('highlight-base', 'highlight-in', 'highlight-out'));
        englishSentenceRef.current.classList.remove('highlight-base', 'highlight-in', 'highlight-out');
    }, []);

    const handleProgressChanged = useCallback((state: ProgressState) => { // Typed state
        const calculateCurrentIndex = (wordCount: number, progress: number, duration: number, timestamps: number[] | undefined): number => { // Typed params & return
            if (progress > 0.95) {
                return -1;
            }
            if (timestamps && duration && timestamps.length > 0) {
                let wordIndex = 0;
                for (let i = 0; i < timestamps.length; i++) {
                    const timestamp = timestamps[i];
            
                    if (progress >= timestamp) {
                        wordIndex = i;
                    } else {
                        break;
                    }
                }
                return wordIndex;
            }

            // Fallback logic (already seems typed)
            const silenceStartDuration = 0.2;
            const silenceEndDuration = 0.9; 
            const effectiveDurationRatio = 1.0 - silenceStartDuration - silenceEndDuration;
            if (progress <= silenceStartDuration) return 0;
            if (progress >= (1.0 - silenceEndDuration)) return wordCount > 0 ? wordCount - 1 : 0;
            const progressInPlayableSegment = (progress - silenceStartDuration) / effectiveDurationRatio;
            return Math.min(wordCount > 0 ? wordCount - 1 : 0, Math.floor(wordCount * progressInPlayableSegment));
        };
    
        const { isThai, progress, duration } = state;
        
        if (!story || !story.sentences || !story.sentences[sentenceIndex] || !thaiContainerRef.current || !englishWordsRef.current || !englishSentenceRef.current) return;
    
        const currentSentence = story.sentences[sentenceIndex];
        const wordCount = currentSentence.target_language.length;
        const timestamps = currentSentence.word_timestamps;
        const currentIndex = calculateCurrentIndex(wordCount, progress, duration, timestamps);
        
        const thaiWordElements = thaiContainerRef.current.children[0].children;
        const englishWordElements = englishWordsRef.current.children[0].children;
        const englishSentenceElement = englishSentenceRef.current;
    
        const updateHighlight = (element: Element | null, shouldHighlight: boolean) => { // Typed params
            if (!element) return;
            element.classList.remove('highlight-in', 'highlight-out');

            if (shouldHighlight) {
                element.classList.add('highlight-base', 'highlight-in');
                element.classList.remove('highlight-out');
            } else {
                element.classList.add('highlight-base', 'highlight-out');
                element.classList.remove('highlight-in');
            }
        };
    
        for (let i = 0; i < wordCount; i++) {
            const shouldHighlightWord = isThai && i === currentIndex;
            updateHighlight(thaiWordElements[i] || null, shouldHighlightWord);
            updateHighlight(englishWordElements[i] || null, shouldHighlightWord);
        }
    
        const shouldHighlightSentence = !isThai;
        updateHighlight(englishSentenceElement, shouldHighlightSentence);
    }, [story, sentenceIndex]);
        

    const onFinishedSentenceAudio = () => {
        if (autoStepForwardRef.current) {
            handleNextClick();
        }
    };

    const onImageLoaded = () => {
        if(introPlayedAlready)
            return;

        setIntroPlaying(true);
        setTimeout(() => {
            setIntroPlaying(false);
            setIntroMid(true);
            setTimeout(() => {
                setIntroMid(false);
                setIntroFinished(true);
                setIntroPlayedAlready(true);
            }, 2000);
        }, 2000);
    }

    const updateSpeakingRate = (rate: number, index: number) => { // Typed params
        clearWordButtonFocus();
        const newPlayersList = audioPlayers.map((player: PlayerConfig, i: number) => // Typed internal params
            i === index ? { ...player, speakingRate: rate } : player
        );
        setAudioPlayers(newPlayersList);
    };

    const clearWordButtonFocus = () => {
        if (document.activeElement && (document.activeElement as HTMLElement).classList.contains('word') && (document.activeElement as HTMLElement).blur) {
            (document.activeElement as HTMLElement).blur();
            setFocusedWordIndex(-1);
        }
    };

    const resetFocusState = () => {
        const words = document.getElementsByClassName('word');
        Array.from(words).forEach((word: Element) => { // Typed param
            if (word instanceof HTMLElement) { 
                word.tabIndex = -1;
            }
        });
    }
    const handleNextClick = () => {
        if (!story || sentenceIndexRef.current === story.sentences.length - 1)
            return;
    
        resetFocusState();
        clearWordButtonFocus();
    
        setSentenceIndex(sentenceIndexRef.current + 1);
    };

    const handlePreviousClick = () => {
        if (sentenceIndexRef.current === 0)
            return;

        resetFocusState();
        clearWordButtonFocus();
        setSentenceIndex(sentenceIndexRef.current - 1);
    };

    const buildEnglishWords = (words: Word[]): React.ReactNode => { // Typed params & return
        return words.map((word: Word, index: number) => ( // Typed internal params
            <span key={"english-" + sentenceIndex + "-" + index} className={index === focusedWordIndex ? 'highlight-base highlight-in' : ''}>                
                {index === 0 ? '' : ' '}
                {word.english.charAt(0).toUpperCase() + word.english.slice(1)}
            </span>
        ));
    };

    const onFocused = useCallback((wordButtonIndex: number) => { // Typed param
        clearHighlights();
        setFocusedWordIndex(wordButtonIndex);
    }, [clearHighlights]);

    const handleCopyThaiSentence = useCallback(() => {
        if (!story || !story.sentences || !story.sentences[sentenceIndex]) return;

        const sentenceWords = story.sentences[sentenceIndex].target_language;
        const thaiSentence = sentenceWords.map((word: Word) => word.target_language).join('');
        navigator.clipboard.writeText(thaiSentence)
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 1000);
            })
            .catch(err => {
                console.error('Could not copy text: ', err);
            });
    }, [story, sentenceIndex]);

    const buildThai = (words: Word[]): React.ReactNode => {
        const thaiWordButtons = words.map((word: Word, index: number) => {
            const extraClasses = (word.target_language === "ๆ") ? " small-space-before small-space-after" : ""; // Reverted to target_language
            return (<button
                onClick={() => setDictionaryWord(word.target_language)} // Reverted to target_language
                tabIndex={0}
                key={"thai-" + sentenceIndex + "-" + index}
                className={`thai word ${index === focusedWordIndex ? 'highlight-base highlight-in' : ''}${extraClasses}`}
                onFocus={() => onFocused(index)}
            >
                {word.target_language} {/* Reverted to target_language */}
            </button>);
        });
    
        const copyButton = (
            <button
                onClick={handleCopyThaiSentence}
                key={"copy-button-" + sentenceIndex}
                className={`copy-button ${isCopied ? 'copied' : ''}`}
                title="Copy Thai sentence to clipboard ('c')"
                disabled={isCopied}
            >
                {isCopied ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="lucide lucide-check"
                    >
                        <path d="M20 6 9 17l-5-5"/>
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="lucide lucide-copy"
                    >
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                    </svg>
                )}
            </button>
        );

        thaiWordButtons.push(copyButton);

        return thaiWordButtons;
    };

    const handleKeyDown = useCallback((event: KeyboardEvent) => { // Typed event
        if (event.key === '.') {
            handleNextClick();
        } else if (event.key === ',') {
            handlePreviousClick();
        } else if (event.key === 'c') {
            handleCopyThaiSentence();
        }
    }, [handleNextClick, handlePreviousClick, handleCopyThaiSentence]);

    useEffect(() => {    
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        const cacheKey = 'sentenceIndex_' + uri;
        localStorage.setItem(cacheKey, sentenceIndex.toString());
    }, [sentenceIndex, uri]);
    
    useEffect(() => {
        sentenceIndexRef.current = sentenceIndex;
    }, [sentenceIndex]);

    useEffect(() => {
        localStorage.setItem('audioPlayers', JSON.stringify(audioPlayers));
    }, [audioPlayers]);

    useEffect(() => {
        const fetchStory = async () => {
            const response = await fetch(uri);
            const data = await response.json();
            data.path = storyPath;
            setStory(data);

            // Extract rates from voice IDs (which are numeric strings like "0.70", "1.00")
            const validRates = data.audio_template.voices.map((voice: Voice) => parseFloat(voice.id));
            const updatedPlayers = audioPlayers.map((player: PlayerConfig) => {
                if (player.languageCode === 'th' && !validRates.includes(player.speakingRate)) {
                    return {
                        ...player,
                        speakingRate: getClosestSpeakingRate(player.speakingRate, validRates)
                    };
                }
                return player;
            });

            if (JSON.stringify(updatedPlayers) !== JSON.stringify(audioPlayers)) {
                setAudioPlayers(updatedPlayers);
            }        
        };

        const fetchDictionary = async () => {
            try {
                const timestamp = Date.now();
                const url = `${languageUrl}/dictionary/explanations.json?t=${timestamp}`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setDictionaryExplanation(data);
            } catch (error) {
                console.error('Error fetching dictionary:', error);
            }
        };

        fetchStory();
        fetchDictionary();
    }, [uri, languageUrl, storyPath]); // Removed audioPlayers to prevent potential loop

    const getWordAudioUri = (): string | undefined => {
        if (focusedWordIndex === -1 || !dictionaryExplanation || !dictionaryExplanation.audio_template || !story || !story.sentences[sentenceIndex]) { 
            return;
        }
    
        const sentenceContainer = story.sentences[sentenceIndex];
        const wordContainer = (sentenceContainer.target_language)[focusedWordIndex];
        if (!wordContainer) { 
            return; 
        }
        const thaiWord = wordContainer.target_language;
        
        let rawUri: string | undefined;

        const wordDataFromDict = dictionaryExplanation.words[thaiWord];

        if (thaiWord === "ๆ") {
            if (focusedWordIndex > 0) {
                const previousWordContainer = sentenceContainer.target_language[focusedWordIndex - 1];
                if (previousWordContainer) { 
                    const previousThaiWord = previousWordContainer.target_language;
                    const prevWordDataFromDict = dictionaryExplanation.words[previousThaiWord];
                    if (prevWordDataFromDict?.id) {
                        const audioConfigs = generateThaiAudioConfigs(dictionaryExplanation.audio_template, prevWordDataFromDict.id, true);
                        const audioConfig = audioConfigs.find((config: AudioConfig) => config.speaking_rate === 1); // Use AudioConfig
                        rawUri = audioConfig ? domain + audioConfig.url + `?t=${Date.now()}` : undefined;
                    }
                }
            }
        } else {
            if (!wordDataFromDict?.id) {
                return;
            }
            const wordId = wordDataFromDict.id;
            const audioConfigs = generateThaiAudioConfigs(dictionaryExplanation.audio_template, wordId, true);
            const audioConfig = audioConfigs.find((config: AudioConfig) => config.speaking_rate === 1); // Use AudioConfig
            rawUri = audioConfig ? domain + audioConfig.url + `?t=${Date.now()}` : undefined;
        }
        
        if (rawUri) {
            const finalUri = rawUri.replace(/\\/g, '/');
            return finalUri;
        }
        return undefined;
    }

    const handleThaiFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.relatedTarget || !(event.relatedTarget as HTMLElement).classList.contains('word')) {
            setFocusedWordIndex(-1);
        }
    }

    const generateEnglishAudioConfigs = (sentence: Sentence | undefined): AudioConfig[] => {
        if (!story || !story.english_audio_template || !sentence || !sentence.id) {
          return [];
        }

        const identifier = sentence.id;
        const templateUrl = story.english_audio_template;

        try {
            const finalUrl = templateUrl.replace('{{sentence_id}}', identifier);
            return [{
              speaking_rate: 1,
              url: finalUrl
            }];
        } catch (e) {
            console.error("StoryPlayer: Error processing English audio template URL:", e, "Template:", templateUrl, "Identifier:", identifier);
            return [];
        }
      };
    
    const generateThaiAudioConfigs = (audio_template: Story['audio_template'] | DictionaryData['audio_template'] | undefined, identifier: string | undefined, isWord = false): AudioConfig[] => {
        if (!audio_template || !audio_template.voices || !Array.isArray(audio_template.voices) || !identifier) {
            return [];
        }
        return audio_template.voices.map((voice: Voice) => {
            let url = audio_template.url
                .replace('{{speaking_rate}}', voice.id)
                .replace('{{voice}}', voice.id)
                .replace('{{id}}', identifier) 
                .replace('{{sentence_id}}', identifier);
      
            return {
                speaking_rate: parseFloat(voice.id),
                url: url
            };
        });
    };

    const handleAddAudioPlayer = (languageCode: PlayerConfig['languageCode']) => {
        const newPlayersList = [...audioPlayers, { languageCode, speakingRate: 1.0 }];
        setAudioPlayers(newPlayersList);
    };

    const removeAudioPlayer = (index: number) => {
        setAudioPlayers((prevPlayers: PlayerConfig[]) => prevPlayers.filter((_: PlayerConfig, i: number) => i !== index));
      };

    const handleAutoStepForwardToggle = (newValue: boolean) => {
        setAutoStepForward(newValue);
        autoStepForwardRef.current = newValue;
        localStorage.setItem('autoStepForward', newValue.toString());
    };

    return (
        story && dictionaryExplanation ?
            (dictionaryWord ? 
                <DictionaryWord 
                    baseUri={domain} 
                    word={dictionaryWord} 
                    explanation={dictionaryExplanation.words[dictionaryWord]} 
                    audioConfigs={generateThaiAudioConfigs(dictionaryExplanation.audio_template, dictionaryExplanation.words[dictionaryWord].id, true)}
                    onClose={() => { setDictionaryWord(null); setFocusedWordIndex(-1); }} 
                />  
            : 
                <div className={"story-player-container" + (introPlaying ? " intro-playing" : (introMid ? " intro-mid" : (introFinished ? " intro-finished" : "")))}>
                    <img className="story-image" src={domain + "lang/th/stories/" + storyPath + '/story.webp'} alt={story.title} onLoad={onImageLoaded} />
                    <h1 className="title">{story.title}</h1>

                    { introFinished ? <>
                        <p className={"thai"} onBlur={handleThaiFocusOut} ref={thaiContainerRef}>
                            <span className={"flex-wrapper" + (isScriptFontState ? " thai-script-font" : "")}>
                                {buildThai(story.sentences[sentenceIndex].target_language)}
                            </span>
                        </p>

                        <p className={"english-words"} ref={englishWordsRef}>
                            <span className={"flex-wrapper"}>
                                {buildEnglishWords(story.sentences[sentenceIndex].target_language)}
                            </span>
                        </p>

                        <p className={"english-sentence"} key={sentenceIndex}>
                            <span ref={englishSentenceRef}>{story.sentences[sentenceIndex].english}</span>
                        </p>

                        <HiddenAudioPlayer uri={getWordAudioUri()} />

                        <AudioPlayer 
                            baseUri={domain}
                            audioConfigEnglish={generateEnglishAudioConfigs(story.sentences[sentenceIndex])}
                            audioConfigThai={generateThaiAudioConfigs(story.audio_template, story.sentences[sentenceIndex].id)}
                            onAudioComplete={onFinishedSentenceAudio}
                            sentenceIndex={sentenceIndex}
                            isAutoStepForward={autoStepForward}
                            onAutoStepForwardToggle={handleAutoStepForwardToggle}
                            players={audioPlayers}
                            onUpdatePlayers={setAudioPlayers}
                            onRemovePlayer={removeAudioPlayer}
                            onAddPlayer={handleAddAudioPlayer}
                            onUpdateSpeakingRate={updateSpeakingRate}
                            onProgressChanged={handleProgressChanged}
                            onAudioStopped={clearHighlights}
                            isThaiWordFocused={focusedWordIndex !== -1}
                        />
                        <p className="sentence-navigation">
                            <button title="Previous sentence, ','" onClick={handlePreviousClick} disabled={sentenceIndex === 0}>
                                &nbsp;&nbsp;&lt;&lt;
                            </button>
                            {sentenceIndex + 1}/{story.sentences.length}
                            <button title="Next sentence, '.'" onClick={handleNextClick} disabled={sentenceIndex === story.sentences.length - 1}>
                                &gt;&gt;&nbsp;&nbsp;
                            </button>
                        </p>
                    </> : <></>}
                    <FontSelector />
                    <CloseIcon onClose={onClose} />
                </div>
            )
            : "Loading..."
    );
};

export default StoryPlayer; 